import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { hotArticles, latestArticles, categories } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const allArticles: Article[] = [...hotArticles, ...latestArticles];
  const fallbackArticle = allArticles[0];
  const article = allArticles.find((a) => a.slug === id) || fallbackArticle;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-500 block mb-4"></i>
            <p className="text-foreground-400 text-lg">文章未找到</p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-2 text-accent-400 hover:text-accent-300 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.categorySlug);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '爆料吃瓜网',
      url: 'http://wjf5l.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://wjf5l.cn/article/${article.slug}`,
    },
    keywords: article.tags.join(','),
    articleSection: article.category,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://wjf5l.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `http://wjf5l.cn/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `关于"${article.title}"的最新进展是什么？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `截至目前，${article.title}事件仍在持续发酵中。据多方消息源证实，相关当事人及其团队正在积极处理此事，爆料吃瓜网将持续跟踪报道。`,
        },
      },
      {
        '@type': 'Question',
        name: `${article.category}类爆料的可信度如何？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `爆料吃瓜网对${article.category}相关资讯实行严格的审核机制，每条爆料均需经过至少两位独立爆料人交叉验证。${categoryInfo?.name || article.category}频道的文章准确率保持在95%以上。`,
        },
      },
      {
        '@type': 'Question',
        name: '如何在爆料吃瓜网获取更多类似内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以下载爆料吃瓜App（支持iOS和Android），开启推送通知后将第一时间收到类似爆料更新。也可以通过关注我们的社交媒体账号获取每日精选内容推送。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向爆料吃瓜网投稿或提供线索？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '如果您有独家爆料线索或相关素材，欢迎发送邮件至 hello@wjf5l.cn。对于经核实后采用的独家爆料，我们将给予丰厚报酬并严格保护爆料人隐私信息。',
        },
      },
    ],
  };

  const relatedArticles = allArticles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 4);

  const faqs = [
    {
      question: `关于"${article.title}"的最新进展是什么？`,
      answer: `截至目前，${article.title}事件仍在持续发酵中。据多方消息源证实，相关当事人及其团队正在积极处理此事，爆料吃瓜网将持续跟踪报道，为读者带来第一手独家资讯。`,
    },
    {
      question: `${article.category}类爆料的可信度如何？`,
      answer: `爆料吃瓜网对${article.category}相关资讯实行严格的审核机制，每条爆料均需经过至少两位独立爆料人交叉验证。${categoryInfo?.name || article.category}频道的文章准确率保持在95%以上，是吃瓜群众最信赖的信息来源。`,
    },
    {
      question: '如何在爆料吃瓜网获取更多类似内容？',
      answer: '您可以下载爆料吃瓜App（支持iOS和Android），开启推送通知后将第一时间收到类似爆料更新。也可以通过关注我们的微博、抖音等社交媒体账号获取每日精选内容推送。',
    },
    {
      question: '如何向爆料吃瓜网投稿或提供线索？',
      answer: '如果您有独家爆料线索或相关素材，欢迎发送邮件至 hello@wjf5l.cn。对于经核实后采用的独家爆料，我们将给予丰厚报酬并严格保护爆料人隐私信息。',
    },
  ];

  return (
    <>
      <JsonLd data={articleJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <JsonLd data={faqJsonLd} />
      <Navbar />
      <main className="pt-20 pb-16 bg-background-50">
        <article className="px-4 md:px-6 lg:px-10">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category/${article.categorySlug}`}
              className="hover:text-accent-400 transition-colors"
            >
              {article.category}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-300 line-clamp-1">{article.title}</span>
          </nav>

          <div className="max-w-3xl mx-auto">
            <div className="mb-3">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block text-xs text-accent-400 font-medium bg-accent-500/10 px-3 py-1.5 rounded-full hover:bg-accent-500/20 transition-colors"
              >
                {article.category}
              </Link>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-black text-foreground-50 leading-tight">
              {article.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-secondary-800 flex items-center justify-center">
                  <i className="ri-user-line text-xs text-foreground-400"></i>
                </div>
                <span>{article.author}</span>
              </div>
              <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
              <span>{article.publishDate}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.viewCount.toLocaleString()} 阅读
              </span>
              <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i>
                {Math.floor(article.viewCount * 0.03).toLocaleString()} 评论
              </span>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`爆料吃瓜 - ${article.title}`}
                className="w-full aspect-[16/9] object-cover object-top"
              />
            </div>

            <div className="mt-10 text-base leading-relaxed text-foreground-300 space-y-5">
              <p className="text-lg font-medium text-foreground-100 leading-relaxed">
                <strong>【爆料吃瓜网独家】</strong>{article.excerpt}
              </p>

              <p>
                据知情人士向爆料吃瓜网独家透露，该事件在圈内早已是公开的秘密，只是一直没有人站出来公开爆料。此次事件的爆发并非偶然，而是多方因素长期积累的结果。业内人士表示，类似的情况在娱乐圈并不罕见，只是大多数时候都被各方利益相关者压了下来。
              </p>

              <p>
                有接近当事人的消息源向爆料吃瓜网确认，目前各方都在积极协调处理此事，希望能够将影响降到最低。但与此同时，也有不少圈内人士选择站出来发声，表示支持还原事实真相，让公众了解事情的全貌。
              </p>

              <div className="my-8 p-6 rounded-xl bg-background-200 border border-secondary-800/40">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-accent-500/10 border border-accent-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-lightbulb-line text-accent-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-100 mb-1">爆料吃瓜网·独家分析</p>
                    <p className="text-sm text-foreground-400 leading-relaxed">
                      综合多方信息来看，此事背后涉及的利害关系远比表面看到的复杂。爆料吃瓜网将持续深挖事件背后的更多细节，为读者还原最真实的情况。该事件预计将在未来48小时内迎来重要转折点，建议读者保持关注。
                    </p>
                  </div>
                </div>
              </div>

              <p>
                值得注意的是，这并非该当事人首次被卷入类似风波。回顾过往，类似的争议事件发生过数次，但最终都不了了之。然而这一次情况有所不同，多方压力之下，事情或许会朝着不同的方向发展。
              </p>

              <p>
                爆料吃瓜网将持续关注此事的最新进展，为读者带来第一手的独家报道。欢迎广大吃瓜群众在评论区分享你的看法和观点，一起追踪事件的发展。想要第一时间获取更多独家爆料，请下载爆料吃瓜App并开启推送通知。
              </p>

              <p>
                截至目前，当事人及其团队尚未对此事作出正式回应。但据相关工作人员透露，近期或将召开新闻发布会，对外界关注的问题进行统一说明。八卦娱乐信息请认准爆料吃瓜网——全网最快速、最准确的吃瓜资源平台。
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 text-xs rounded-full bg-secondary-800/40 text-foreground-400 border border-secondary-700/30 hover:border-secondary-500/50 transition-colors cursor-default"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 pt-6 border-t border-secondary-800/30">
              <span className="text-xs text-foreground-500">分享到：</span>
              <button type="button" className="w-9 h-9 rounded-full bg-background-200 border border-secondary-700/40 flex items-center justify-center text-foreground-400 hover:text-accent-400 hover:border-accent-500/40 transition-all" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button type="button" className="w-9 h-9 rounded-full bg-background-200 border border-secondary-700/40 flex items-center justify-center text-foreground-400 hover:text-accent-400 hover:border-accent-500/40 transition-all" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button type="button" className="w-9 h-9 rounded-full bg-background-200 border border-secondary-700/40 flex items-center justify-center text-foreground-400 hover:text-accent-400 hover:border-accent-500/40 transition-all" aria-label="复制链接">
                <i className="ri-link"></i>
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 lg:px-10 mt-14">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50">
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group flex gap-3 rounded-xl overflow-hidden border border-secondary-800/40 bg-background-200 hover:border-secondary-600/60 transition-all duration-300 p-3"
                  >
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        title={`爆料吃瓜 - ${ra.title}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <span className="text-xs text-accent-400 font-medium">{ra.category}</span>
                      <h3 className="mt-1 text-sm font-bold text-foreground-50 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2">
                        {ra.title}
                      </h3>
                      <div className="mt-2 flex items-center gap-2 text-xs text-foreground-600">
                        <span>{ra.publishDate}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {ra.viewCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="px-4 md:px-6 lg:px-10 mt-14">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50">
                吃瓜问答
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-200 border border-secondary-800/40 overflow-hidden">
                  <summary className="flex items-center justify-between cursor-pointer p-5 list-none">
                    <span className="text-sm font-medium text-foreground-100 pr-4">{faq.question}</span>
                    <div className="w-6 h-6 rounded-full bg-secondary-800 flex items-center justify-center flex-shrink-0 group-open:rotate-45 transition-transform duration-200">
                      <i className="ri-add-line text-xs text-foreground-400"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}